import { useCallback, useEffect, useRef, useState } from 'react';
import { AUTOSAVE_DEBOUNCE_MS, RETRY_BACKOFF_MS } from '../lib/autosave-config';
import { deserializeDraft } from '../lib/draft-serde';
import type { AutosaveState, DraftPatch, WireDraft } from '../types/draft';

type ApiLikeError = Error & {
  status?: number;
  body?: {
    code?: string;
    message?: string;
    fieldErrors?: Record<string, string>;
    current?: WireDraft;
  };
};

/** How one save is sent. `keepalive` outlives a closing tab (NPJB-839 #103). */
export interface SaveOptions {
  keepalive?: boolean;
}

export interface UseAutosaveArgs {
  save: (patch: DraftPatch, options?: SaveOptions) => Promise<unknown>;
}

export interface UseAutosaveReturn {
  state: AutosaveState;
  markDirty: (patch: DraftPatch) => void;
  flushNow: (options?: SaveOptions) => Promise<void>;
  /** True while an edit is waiting to be saved (debounced, queued or failed). */
  hasPendingChanges: () => boolean;
  /** Drops every queued edit and the debounce timer. Call after the draft is submitted. */
  discardPending: () => void;
  acknowledgeConflictResolved: () => void;
  isSaveInFlight: boolean;
  getCurrentStateKind: () => AutosaveState['kind'];
}

/** 0 is a fresh save; 1–3 are retries. */
type SaveAttempt = 0 | 1 | 2 | 3;

/** The state a failed save stops in, or null when the failure is worth a retry. */
function stopStateFor(err: ApiLikeError): AutosaveState | null {
  switch (err.status ?? 0) {
    // A stale-draft 409 carries no server copy today (B15); the modal then offers reload.
    case 409:
      return {
        kind: 'conflict',
        serverDraft: err.body?.current ? deserializeDraft(err.body.current) : null,
      };
    case 403:
      return { kind: 'error_terminal', message: err.body?.message ?? 'Forbidden' };
    case 422:
      return { kind: 'error_validation', fieldErrors: err.body?.fieldErrors ?? {} };
    case 401:
      return { kind: 'error_transient', message: 'Session expired' };
    default:
      return null;
  }
}

/** A mutable `{ current }` box. A React ref is one; tests pass plain objects. */
interface Box<T> {
  current: T;
}

/** The parts of the autosave state a page-exit save takes over. */
export interface ExitSaveSlots {
  /** The patch of the normal save now in flight. Empty once a keepalive save owns it. */
  inFlightPatch: Box<DraftPatch>;
  /** Edits queued since that save started. */
  pending: Box<DraftPatch>;
  dirtyAgain: Box<boolean>;
  /** Bumped so the in-flight run no longer re-queues, retries or reports a failure. */
  generation: Box<number>;
}

const hasKeys = (patch: DraftPatch) => Object.keys(patch).length > 0;

/**
 * The page is closing while a normal save is in flight (NPJB-839). The browser
 * cancels that save, and its patch already left the queue. So send the in-flight
 * patch plus every queued edit as ONE keepalive save, right now. Never await here:
 * the page may be gone by the next tick.
 *
 * `save` reads the last-seen token at call time, the same as any save. The old run
 * is retired, so a 409 it gets from racing this save is not shown. If this save
 * fails, its patch goes back to the queue under newer edits and `onRequeued` runs.
 *
 * Returns the keepalive save, or null when there is nothing left to send (a second
 * exit event after the first took everything).
 */
export function sendExitSaveOverInFlight(
  slots: ExitSaveSlots,
  save: UseAutosaveArgs['save'],
  options: SaveOptions,
  { onSaved, onRequeued }: { onSaved: () => void; onRequeued: () => void },
): Promise<void> | null {
  const patch = { ...slots.inFlightPatch.current, ...slots.pending.current };
  if (!hasKeys(patch)) return null;
  slots.inFlightPatch.current = {};
  slots.pending.current = {};
  slots.dirtyAgain.current = false;
  slots.generation.current += 1;
  return save(patch, options).then(onSaved, () => {
    slots.pending.current = { ...patch, ...slots.pending.current };
    onRequeued();
  });
}

export function useAutosave({ save }: UseAutosaveArgs): UseAutosaveReturn {
  const [state, setState] = useState<AutosaveState>({ kind: 'idle', lastSavedAt: null });
  const stateRef = useRef<AutosaveState>({ kind: 'idle', lastSavedAt: null });
  const pendingRef = useRef<DraftPatch>({});
  const dirtyAgainRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef(false);
  const inFlightPromiseRef = useRef<Promise<void> | null>(null);
  // The in-flight normal save's patch, kept so a page-exit save can resend it.
  const inFlightPatchRef = useRef<DraftPatch>({});
  const runSaveRef = useRef<(attempt: SaveAttempt) => Promise<void>>(null!);
  // Bumped by discardPending: a save that started before it must not re-queue its patch,
  // retry, or set state when it settles (NPJB-839 — Reload Latest during a save).
  const generationRef = useRef(0);
  // A page-exit save succeeded while a retired save was still in flight. That run's
  // failure is swallowed, so it reports `saved` instead (NPJB-839).
  const exitSavedRef = useRef(false);

  const writeState = useCallback((next: AutosaveState) => {
    stateRef.current = next;
    setState(next);
  }, []);

  const clearDebounce = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  };

  const scheduleDebounced = useCallback(() => {
    clearDebounce();
    debounceRef.current = setTimeout(() => {
      void runSaveRef.current(0);
    }, AUTOSAVE_DEBOUNCE_MS);
  }, []);

  const runSave = useCallback(
    async (attempt: SaveAttempt, options?: SaveOptions) => {
      if (inFlightRef.current) {
        if (inFlightPromiseRef.current) await inFlightPromiseRef.current;
        return;
      }
      // This run takes every queued edit, so a still-armed debounce would only save `{}`.
      clearDebounce();
      // Nothing queued (a retry timer that outlived discardPending): send nothing, or an
      // empty PATCH bumps the row and makes other tabs' tokens stale.
      if (Object.keys(pendingRef.current).length === 0) return;
      const patch = pendingRef.current;
      pendingRef.current = {};
      const generation = generationRef.current;
      inFlightRef.current = true;
      // A keepalive save already survives the tab closing, so a page exit has nothing to resend.
      inFlightPatchRef.current = options?.keepalive ? {} : patch;
      writeState(
        attempt === 0 ? { kind: 'saving' } : { kind: 'retrying', attempt: attempt as 1 | 2 | 3 },
      );
      // An edit made while this save was in flight goes out next.
      const resumeIfDirtyAgain = () => {
        if (!dirtyAgainRef.current) return;
        dirtyAgainRef.current = false;
        writeState({ kind: 'dirty' });
        scheduleDebounced();
      };
      // A retry belongs to this run's generation; after discardPending it must not fire.
      const retryLater = (next: SaveAttempt, delay: number) =>
        setTimeout(() => {
          if (generation === generationRef.current) void runSaveRef.current(next);
        }, delay);
      const work = (async () => {
        try {
          await save(patch, options);
          writeState({ kind: 'saved', at: new Date().toISOString() });
          resumeIfDirtyAgain();
        } catch (e) {
          if (generation !== generationRef.current) {
            // Discarded while in flight: drop this patch, but still save an edit made since.
            if (exitSavedRef.current) writeState({ kind: 'saved', at: new Date().toISOString() });
            resumeIfDirtyAgain();
            return;
          }
          const err = e as ApiLikeError;
          pendingRef.current = { ...patch, ...pendingRef.current };
          const stopState = stopStateFor(err);
          if (stopState) {
            writeState(stopState);
            return;
          }
          if (attempt < 3) {
            retryLater((attempt + 1) as 1 | 2 | 3, RETRY_BACKOFF_MS[attempt as 0 | 1 | 2]);
          } else {
            writeState({
              kind: 'error_transient',
              message: err.body?.message ?? 'Could not save',
            });
            retryLater(0, RETRY_BACKOFF_MS[2]);
          }
        }
      })();
      inFlightPromiseRef.current = work;
      try {
        await work;
      } finally {
        inFlightRef.current = false;
        inFlightPromiseRef.current = null;
        inFlightPatchRef.current = {};
        exitSavedRef.current = false;
      }
    },
    [save, scheduleDebounced, writeState],
  );

  const markDirty = useCallback(
    (patch: DraftPatch) => {
      pendingRef.current = { ...pendingRef.current, ...patch };
      if (inFlightRef.current) {
        dirtyAgainRef.current = true;
        return;
      }
      writeState({ kind: 'dirty' });
      scheduleDebounced();
    },
    [scheduleDebounced, writeState],
  );

  /** Page exit: send now, never await (NPJB-839). See sendExitSaveOverInFlight. */
  const sendPageExitSave = useCallback(
    (options: SaveOptions) => {
      if (!inFlightRef.current) {
        // runSave sends synchronously; only the response is awaited.
        void runSave(0, options);
        return;
      }
      const slots: ExitSaveSlots = {
        inFlightPatch: inFlightPatchRef,
        pending: pendingRef,
        dirtyAgain: dirtyAgainRef,
        generation: generationRef,
      };
      void sendExitSaveOverInFlight(slots, save, options, {
        onSaved: () => {
          if (inFlightRef.current) {
            exitSavedRef.current = true;
            return;
          }
          writeState({ kind: 'saved', at: new Date().toISOString() });
        },
        // The tab stayed (it was only hidden): save the patch again the normal way, which
        // retries and shows a real conflict.
        onRequeued: () => {
          if (inFlightRef.current) {
            dirtyAgainRef.current = true;
            return;
          }
          writeState({ kind: 'dirty' });
          scheduleDebounced();
        },
      });
    },
    [runSave, save, scheduleDebounced, writeState],
  );

  const flushNow = useCallback(
    async (options?: SaveOptions) => {
      clearDebounce();
      if (options?.keepalive) {
        sendPageExitSave(options);
        return;
      }
      if (inFlightRef.current) {
        if (inFlightPromiseRef.current) await inFlightPromiseRef.current;
      }
      if (!hasKeys(pendingRef.current)) return;
      await runSave(0, options);
    },
    [runSave, sendPageExitSave],
  );

  const hasPendingChanges = useCallback(
    () => debounceRef.current !== null || Object.keys(pendingRef.current).length > 0,
    [],
  );

  const discardPending = useCallback(() => {
    clearDebounce();
    pendingRef.current = {};
    dirtyAgainRef.current = false;
    generationRef.current += 1;
  }, []);

  const acknowledgeConflictResolved = useCallback(() => {
    writeState({ kind: 'idle', lastSavedAt: new Date().toISOString() });
  }, [writeState]);

  useEffect(() => {
    runSaveRef.current = runSave;
  }, [runSave]);

  // Leaving the wizard inside the debounce window must not drop the last edit (B17).
  // Fire the save now; the local mirror stays as the safety net if it fails.
  // runSave clears the timer itself and returns early when a save is already in flight.
  useEffect(
    () => () => {
      clearDebounce();
      if (Object.keys(pendingRef.current).length === 0) return;
      void runSaveRef.current(0);
    },
    [],
  );

  const getCurrentStateKind = useCallback(() => stateRef.current.kind, []);

  return {
    state,
    markDirty,
    flushNow,
    hasPendingChanges,
    discardPending,
    acknowledgeConflictResolved,
    isSaveInFlight: state.kind === 'saving' || state.kind === 'retrying',
    getCurrentStateKind,
  };
}
